import type {
  EntityActivityCheckDocumentType,
  EntityActivitySourceType,
  EntityActivityType,
} from "#graphql/supergraphTypes";

// Display labels for fay's activity vocabularies. Each map is total against the
// generated type, so a member added to the schema fails this build until it is
// labeled — but every lookup still goes through an accessor that falls back to
// the raw wire value, because the backend can start emitting a new member before
// this package's generated types catch up (the same expand/contract gap
// `getCellPresentation` guards). Degrading to the raw value beats rendering
// blank.

const ACTIVITY_TYPE_LABELS: Record<EntityActivityType, string> = {
  INVESTMENT_TRANSACTION: "Investment transaction",
  TRANSFER: "Transfer",
  VALUATION_UPDATE: "Valuation update",
};

export function activityTypeLabel(value: EntityActivityType): string {
  return ACTIVITY_TYPE_LABELS[value] ?? value;
}

const SOURCE_TYPE_LABELS: Record<EntityActivitySourceType, string> = {
  JIRA: "Jira",
};

export function sourceTypeLabel(value: EntityActivitySourceType): string {
  return SOURCE_TYPE_LABELS[value] ?? value;
}

/** The document type a review note was evaluated against. Named apart from
 *  `classificationGrouping`'s `docTypeLabel`, which labels a package document's
 *  classified type from a different vocabulary. */
const REVIEW_NOTE_DOC_TYPE_LABELS: Record<EntityActivityCheckDocumentType, string> = {
  FINANCIAL_STATEMENT: "Financial statement",
  PCAP: "PCAP",
  WORKBOOK: "Workbook",
  UNKNOWN: "Unclassified document",
};

export function reviewNoteDocTypeLabel(value: EntityActivityCheckDocumentType): string {
  return REVIEW_NOTE_DOC_TYPE_LABELS[value] ?? value;
}

/**
 * Formats a review note's evaluation timestamp in UTC, suffixed with `UTC` so
 * the zone is never ambiguous. Rendering in one fixed zone rather than the
 * viewer's local one keeps the displayed instant identical for every reviewer,
 * matches the stored server timestamp, and makes the output independent of the
 * runtime timezone. An unparseable value renders verbatim rather than as
 * "Invalid Date".
 */
export function formatActivityDateTime(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) {
    return iso;
  }
  // Narrow/no-break spaces from Intl vary by ICU build; normalize to a plain space.
  const formatted = parsed
    .toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short", timeZone: "UTC" })
    .replace(/[\u202f\u00a0]/g, " ");
  return `${formatted} UTC`;
}
